import { FloatingPortal } from "@floating-ui/react";
import { type FC, useMemo, useRef, useState } from "react";
import { useStore } from "react-redux";

import { useBackgroundLocation } from "../../common/home/components/UseBackgroundLocation";
import { useAppNavigate } from "../../common/hooks/useAppNavigate";
import {
  type TrackerCategory,
  trackEvent,
} from "../../common/tracking/trackerService";
import { zIndices } from "../../common/zIndices";
import type { StoreState } from "../../store/storeState";
import { SkipFooter } from "./SkipFooter";
import { Step } from "./Step";
import { StepFooter } from "./StepFooter";
import { type LifecycleEffectsParams, Tour, type TourDefinition } from "./Tour";

interface PortalTourProps {
  active: boolean;
  mainTour: TourDefinition;
  onFinished: () => void;
  skipTour: TourDefinition;
  trackerCategory: TrackerCategory;
}

export const getTourRoot = (): HTMLElement => {
  const container = document.getElementById("tour-root");
  if (!container) {
    console.error(`Target element "#tour-root" is not in dom`);

    return document.body;
  }
  return container;
};

export const PortalTour: FC<PortalTourProps> = ({
  mainTour,
  skipTour,
  active,
  onFinished,
  trackerCategory,
}) => {
  const store = useStore<StoreState>();
  const { replace } = useAppNavigate();
  const location = useBackgroundLocation();

  const lifecycleEffectsRef = useRef<LifecycleEffectsParams>({
    store,
    replace,
    location,
  });

  const [showSkipStep, setShowSkipStep] = useState(false);

  const showTour = active || showSkipStep;

  const tour = useMemo(
    () => (showSkipStep ? skipTour : mainTour),
    [mainTour, showSkipStep, skipTour],
  );

  const onTourFinished = (index: number, skipped: boolean) => {
    if (!showSkipStep) {
      if (skipped) {
        trackEvent({
          category: trackerCategory,
          action: "Skip",
          label: `At card ${index + 1}`,
        });
        setShowSkipStep(true);
      } else {
        trackEvent({ category: trackerCategory, action: "Finished" });
      }
      onFinished();
    } else {
      setShowSkipStep(false);
    }
  };

  const onTourError = () => {
    trackEvent({ category: trackerCategory, action: "Error" });
    setShowSkipStep(false);
    onFinished();
  };

  return (
    <FloatingPortal root={getTourRoot()}>
      {showTour && (
        <Tour
          zIndex={zIndices.tourOverlay}
          lifecycleEffectsRef={lifecycleEffectsRef}
          steps={tour.steps}
          onFinished={onTourFinished}
          onError={onTourError}
          renderStep={(props) => (
            <Step
              {...props}
              renderFooter={(footerProps) =>
                showSkipStep ? (
                  <SkipFooter {...footerProps} />
                ) : (
                  <StepFooter {...footerProps} />
                )
              }
            />
          )}
        />
      )}
    </FloatingPortal>
  );
};
